// src/components/utils/documentHelpers.js
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

// Функция генерирующая нумер контракта
export const generateContractNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${year}/${month}/${day}/${random}`;
};

// Функция выциягающая город из адреса
export const extractCityFromAddress = (address) => {
    if (!address) return null;
    
    const patterns = [
        /\d{2}-\d{3}\s+([^,]+)/,        // код почтовый + город
        /,\s*([^,]+)\s*$/,              // последняя часть после запятой
        /ul\.\s+[^,]+,\s*([^,]+)/,      // после улицы
    ];
    
    for (const pattern of patterns) {
        const match = address.match(pattern);
        if (match) {
            return match[1].trim();
        }
    }
    
    return null;
};

// Функция форматирующая дату по-польски
export const formatPolishDate = (date) => {
    const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    };
    return date.toLocaleDateString('pl-PL', options);
};

/**
 * Функция подготавливающая данные для замены placeholder'ов
 * @param {string} userId - ID пользователя
 * @param {boolean} includeSignature - Добавить ли подпись
 * @param {string} signatureUrl - URL изображения с подписью пользователя
 * @param {Object} additionalData - Дополнительные данные (например, подпись фирмы)
 */
export const prepareReplacementData = async (userId, includeSignature = false, signatureUrl = null, additionalData = {}) => {
    try {
        // Получить основные данные пользователя
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
            throw new Error(`Пользователь ${userId} не существует`);
        }
        
        const userMainData = userDoc.data() || {};
        
        // Получить персональные данные
        const danePersonalneDoc = await getDoc(
            doc(db, 'users', userId, 'dokumenty', 'dane_personalne')
        );
        const personalData = danePersonalneDoc.exists() ? danePersonalneDoc.data() : {};
        
        // Получить данные автомобиля
        const carDoc = await getDoc(
            doc(db, 'users', userId, 'dokumenty', 'samochod')
        );
        const carData = carDoc.exists() ? carDoc.data() : {};
        
        // Подготовить данные
        const currentDate = formatPolishDate(new Date());
        const contractNumber = generateContractNumber();
        const fullName = `${userMainData.firstName || ''} ${userMainData.lastName || ''}`.trim() || 'Nie określono';
        
        // Адрес
        const address = personalData.registrationAddress || 
                       personalData.correspondenceAddress || 
                       personalData.residenceAddress ||
                       personalData.street ||
                       'Nie określono';
                       
        const city = personalData.city || extractCityFromAddress(address) || 'Nie określono';
        const postalCode = personalData.postalCode || '';
        
        let fullAddress = address;
        if (city !== 'Nie określono' && !address.includes(city)) {
            fullAddress = `${address}, ${postalCode} ${city}`.replace(/,\s*,/g, ',').trim();
        }
        
        // ПОДГОТОВИТЬ ПОДПИСИ - ТОЛЬКО ДВА PLACEHOLDER'А
        let driverSignatureHTML = '<div class="signature-line"></div>';
        let companySignatureHTML = '<div class="signature-line"></div>';
        
        // ПОДПИСЬ ВОДИТЕЛЯ/ПОЛЬЗОВАТЕЛЯ - УМЕНЬШЕННЫЙ РАЗМЕР
        if (includeSignature && signatureUrl) {
            driverSignatureHTML = `<div class="signature-container"><img src="${signatureUrl}" class="signature-image" style="height: 40px; max-width: 100px; display: block; margin: 5px 0;" alt="Podpis kierowcy" /></div>`;
        }
        
        // ПОДПИСЬ ФИРМЫ - ИСПРАВЛЕНО
        if (includeSignature) {
            const companySignatureUrl = 'https://firebasestorage.googleapis.com/v0/b/ccmcolorpartner.appspot.com/o/images%2F1751601259392_%D0%9A%D0%BE%D0%BF%D0%B8%D1%8F%20piecza%CC%A8tka.jpeg?alt=media&token=c9f82822-77d9-4d84-ac7c-451c103229d8';
            companySignatureHTML = `<div class="signature-container"><img src="${companySignatureUrl}" class="signature-image" style="height: 80px; max-width: 100px; display: block; margin: 5px 0;" alt="Pieczątka firmy" /></div>`;
        }
        
        // Объект со ВСЕМИ placeholder'ами
        const replacements = {
            // Основные данные
            'PLACEHOLDER_FIRST_NAME': userMainData.firstName || '',
            'PLACEHOLDER_LAST_NAME': userMainData.lastName || '',
            'PLACEHOLDER_DRIVER_NAME': fullName,
            'PLACEHOLDER_DRIVER_ADDRESS': fullAddress,
            'PLACEHOLDER_DRIVER_CITY': city,
            'PLACEHOLDER_DRIVER_PESEL': personalData.pesel || 'Nie określono',
            'PLACEHOLDER_REGISTRATION_ADDRESS': fullAddress,
            
            // Даты
            'PLACEHOLDER_DATE': currentDate,
            'PLACEHOLDER_CURRENT_DATE': currentDate,
            'PLACEHOLDER_CONTRACT_DATE': currentDate,
            
            // Номер контракта
            'PLACEHOLDER_CONTRACT_NUMBER': contractNumber,
            
            // Данные фирмы
            'PLACEHOLDER_COMPANY_NAME': 'COLOR PARTNER sp. z o.o.',
            'PLACEHOLDER_COMPANY_NIP': '8943198563',
            'PLACEHOLDER_COMPANY_ADDRESS': 'ul. Grabiszyńska 214/7, 53-235 Wrocław',
            'PLACEHOLDER_COMPANY_KRS': '0001001466',
            
            // Данные автомобиля
            'PLACEHOLDER_CAR_MAKE': carData.carMake || 'Nie określono',
            'PLACEHOLDER_CAR_MODEL': carData.carModel || 'Nie określono',
            'PLACEHOLDER_CAR_YEAR': carData.carYear || 'Nie określono',
            'PLACEHOLDER_CAR_VIN': carData.carVin || 'Nie określono',
            'PLACEHOLDER_CAR_REGISTRATION_NUMBER': carData.carRegistrationNumber || 'Nie określono',
            
            // Гражданство
            'PLACEHOLDER_CITIZENSHIP': personalData.citizenship || userMainData.citizenship || 'Polska',
            
            // ТОЛЬКО ДВА PLACEHOLDER'А ДЛЯ ПОДПИСЕЙ
            'PLACEHOLDER_SIGNATURE_DRIVER': driverSignatureHTML,
            'PLACEHOLDER_SIGNATURE_COMPANY': companySignatureHTML
        };
        
        return replacements;
    } catch (error) {
        throw error;
    }
};

// Функция заменяющая placeholder'ы
export const replacePlaceholders = (html, replacements) => {
    let processedHtml = html;
    
    // Сортировать от самых длинных к самым коротким
    const sortedKeys = Object.keys(replacements).sort((a, b) => b.length - a.length);
    
    sortedKeys.forEach(placeholder => {
        const value = replacements[placeholder] || '';
        // Заменить все вхождения
        processedHtml = processedHtml.replace(new RegExp(placeholder, 'g'), value);
    });
    
    return processedHtml;
};